import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import {
  AD_SANDBOX, adBreak, adEvent, adFrameSrc, ads, beforeTable, book, flushAds, noteMatchEnd, noteRoom,
  readAdMessage, resultTag, setAdSender, type Break, type Place,
} from "../net/ads";
import { faNum } from "../game/cards";
import { Overlay } from "./Overlay";

/**
 * تبلیغِ وب روی صفحه — منطقش در `net/ads.ts` است، این‌جا فقط کارت و بنر.
 *
 * ویجتِ مدیااد در قابِ sandbox ِ `/ad.html` پر می‌شود و نه روی این صفحه — [[AD_SANDBOX]].
 * قاب ارتفاعِ آگهی را می‌فرستد و همان ارتفاع روی خودِ قاب می‌نشیند.
 */

/** «پر شد» = لودر چیزی با اندازه‌ی واقعی در ویجت گذاشت. لودر رویدادی برای
 *  این ندارد؛ ویجتِ بی‌تبلیغ خالی و بی‌ارتفاع می‌مانَد. */
const FILLED_PX = 40;

/**
 * قابِ یک ویجت. ارتفاعش همان است که قاب می‌گوید؛ تا آمدنِ اولین اندازه یک پیکسل، تا
 * آگهیِ نیامده جای خالی نسازد و قاب هم هنوز «روی صفحه» باشد.
 */
function AdFrame({ domain, tag, onHeight }: { domain: string; tag: string; onHeight: (h: number) => void }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [h, setH] = useState(0);
  // پهنای بنرِ ثابت: یک بار قفل می‌شود. پس از جمع‌شدنِ قاب، بنر دیگر از قاب باریک‌تر نیست و صفر
  // می‌آید — آن صفر قاب را دوباره پهن نمی‌کند (وگرنه پهن و جمع می‌شد).
  const [w, setW] = useState(0);
  const tell = useRef(onHeight);
  tell.current = onHeight;
  useEffect(() => {
    const on = (e: MessageEvent) => {
      const m = readAdMessage(e, ref.current?.contentWindow);
      if (!m) return;
      setH(m.h);
      if (m.w > 0) setW((x) => x || m.w);
      tell.current(m.h);
    };
    window.addEventListener("message", on);
    return () => window.removeEventListener("message", on);
  }, []);
  return (
    <iframe ref={ref} className="adframe" title="آگهی" sandbox={AD_SANDBOX}
      src={adFrameSrc(domain, tag)} style={{ height: Math.max(h, 1), ...(w ? { width: w } : {}) }} />
  );
}

/**
 * شروعِ میز از منو، با کارتِ مکث اگر قاعده اجازه بدهد — {@link beforeTable}. `place` همان کلیدِ
 * روشن/خاموشِ پنل است (بازیِ سریع، ربات، جام، تماشا).
 */
export function playAfterAd(start: () => void, place: Place = "quick"): void {
  beforeTable(start, client.subscription.value?.active === true, undefined, place);
}

/** کارتِ مکثِ پیش از میز؛ یک بار در App. */
export function AdBreak() {
  const b = useSignal(adBreak);
  return b ? <BreakCard key={b.id} b={b} /> : null;
}

function BreakCard({ b }: { b: Break }) {
  const height = useRef(0);
  const [shown, setShown] = useState(false);
  const [left, setLeft] = useState(Math.ceil(b.holdMs / 1000));

  // پرشد یا نه؟ تا مهلتِ پنل (web_fill_ms). نه = کارت بسته و میز همان لحظه.
  useEffect(() => {
    if (shown) return;
    const t0 = Date.now();
    const iv = window.setInterval(() => {
      if (height.current >= FILLED_PX) {
        window.clearInterval(iv);
        book.noteShown();
        adEvent("ad_start", "WB", { net: "mediaad", at: b.place });
        setShown(true);
      } else if (Date.now() - t0 >= b.fillMs) {
        window.clearInterval(iv);
        adEvent("ad_fail", "WB", { why: "no_fill", at: b.place });
        b.go();
      }
    }, 100);
    return () => window.clearInterval(iv);
  }, [b, shown]);

  // شمارشِ معکوسِ «شروعِ بازی».
  useEffect(() => {
    if (!shown || left <= 0) return;
    const t = window.setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => window.clearTimeout(t);
  }, [shown, left]);

  const ready = shown && left <= 0;
  return (
    <Overlay base="veil" className="adbreak" label="آگهی پیش از بازی">
      <div className={"box adbox" + (shown ? " on" : "")}>
        <div className="adhead">
          <span className="adtag">آگهی</span>
          <span className="muted small">{shown ? "میز بعد از این آگهی" : "میز آماده می‌شود…"}</span>
        </div>
        <div className="adwell"><AdFrame domain={b.domain} tag={b.tag} onHeight={(h) => { height.current = h; }} /></div>
        <button className="btn" disabled={!ready} onClick={() => { adEvent("ad_complete", "WB", { at: b.place }); b.go(); }} aria-live="polite">
          {ready ? "شروع بازی" : shown ? `شروع بازی · ${faNum(left)}` : "…"}
        </button>
      </div>
    </Overlay>
  );
}

/**
 * بنرِ لابی. مشترک نمی‌بیندش. `slot` شماره‌ی جاست — ۱ زیرِ قفسه‌ها، ۲ کنارِ بازیِ انتخاب‌شده، ۳ میانِ
 * قفسه‌ها — و فقط تا `web_lobby_slots` ِ پنل پر می‌شود (کاربر، ۱ نوامبر ۲۰۲۶: «میزانِ تبلیغاتِ این صفحه
 * را بیشتر کن»). همه همان ویجتِ web_lobby اند، هر کدام در قابِ خودش.
 */
export function LobbyAd({ slot = 1 }: { slot?: 1 | 2 | 3 }) {
  const a = useSignal(ads);
  const sub = useSignal(client.subscription);
  const [on, setOn] = useState(false);
  const tag = a && !sub?.active && slot <= a.lobbySlots ? a.lobby : "";

  // برچسبِ «آگهی» فقط وقتی چیزی آمد — جای خالی با برچسب، حفره‌ای بی‌دلیل است.
  useEffect(() => { setOn(false); }, [tag]);
  useEffect(() => { if (on) adEvent("ad_start", "WL", { net: "mediaad", at: `slot${slot}` }); }, [on, slot]);

  if (!a || !tag) return null;
  return (
    // «lobbyad»: قابِ هم‌زبانِ لابی دورِ آگهی (lux.css) — آگهیِ پایانِ میز همان adslot است و قابش را نمی‌گیرد.
    <aside className={"adslot lobbyad" + (on ? " on" : "")} aria-label="آگهی" data-slot={slot}>
      <span className="adtag">آگهی</span>
      <AdFrame key={tag} domain={a.domain} tag={tag} onHeight={(h) => setOn(h >= FILLED_PX)} />
    </aside>
  );
}

// ───────────────────────── زمانِ سرِ میز ─────────────────────────

/** هر چند وقت زمانِ سرِ میز در دفتر نوشته شود. */
const TICK_MS = 5000;

/**
 * ساعتِ میز و نشانِ «اتاقِ دوستان» — یک بار در App.
 *
 * کارتِ پیش از میز بر پایه‌ی زمانِ بازی است ([[AdBook.notePlay]]): فقط وقتی میزی باز است
 * (بازی یا تماشا) **و** زبانه دیده می‌شود. لابی، صفحه‌ی پنهان و خوابِ لپ‌تاپ بازی نیستند.
 */
export function AdClock({ atTable, inRoom }: { atTable: boolean; inRoom: boolean }) {
  const a = useSignal(ads);
  // رویدادهای آگهیِ وب به همان /v1/events ِ اپ — با توکنِ همین بازیکن؛ بی‌حساب یا بی‌شبکه بی‌صدا.
  useEffect(() => {
    setAdSender((events) => { void client.rest("POST", "/v1/events", { events }).catch(() => {}); });
    const away = () => flushAds();
    window.addEventListener("pagehide", away);
    return () => { window.removeEventListener("pagehide", away); setAdSender(null); };
  }, []);
  useEffect(() => { if (inRoom) noteRoom(); }, [inRoom]);
  useEffect(() => {
    if (!atTable || !a) return;
    let last = Date.now();
    const tick = () => {
      const now = Date.now();
      if (document.visibilityState === "visible") book.notePlay(now - last);
      last = now;
    };
    const iv = window.setInterval(tick, TICK_MS);
    return () => { window.clearInterval(iv); tick(); };
  }, [atTable, a]);
  return null;
}

// ───────────────────────── پایانِ میز ─────────────────────────

/** شناسه‌ی میزِ جاری — برای این‌که نتیجه‌ی هر مسابقه یک بار شمرده شود. */
function tableKey(): string {
  return client.table.value?.tableId ?? client.cbTable.value?.tableId ?? client.shTable.value?.tableId ??
    client.hkTable.value?.tableId ?? client.board.value?.tableId ?? client.mafia.value?.tableId ?? "";
}

/** فاصله‌ی آگهی از کارتِ نتیجه (فاصله از کفِ صفحه با ناحیه‌ی امنِ گوشی در style.css است). */
const GAP_PX = 16;
/** کم‌تر از این جا زیرِ کارت، آگهی اصلاً بارگیری نمی‌شود. */
const MIN_ROOM_PX = 72;

/**
 * آگهیِ پنلِ پایانِ مسابقه (`web_result`) — تصمیمِ کاربر، ۲۱ اکتبر ۲۰۲۶. بی انتظار و بی
 * شمارش: نتیجه همان لحظه دیده می‌شود و دکمه‌ی برگشت همان‌جاست که بود.
 *
 * بیرونِ کارت و چسبیده به کفِ پرده، نه زیرِ دکمه در جریانِ کارت: کارتِ وسط‌چین با آمدنِ
 * آگهی بالا می‌پرید و لمسی که برای «بازگشت» رفته بود روی آگهی می‌نشست. پس جای خالیِ زیرِ
 * کارت اندازه گرفته می‌شود؛ کم‌تر از {@link MIN_ROOM_PX} یعنی هیچ قابی ساخته نمی‌شود (نه
 * آگهیِ پنهان و دیده‌نشده)، و اگر آگهیِ آمده بلندتر از جا بود — یا کارت بعداً بلندتر شد
 * (نتیجه‌ی جام) — برداشته می‌شود و دیگر برنمی‌گردد.
 *
 * همین‌جا نتیجه‌ی مسابقه هم برای «باختِ پشتِ هم» ِ کارتِ پیش از میز ثبت می‌شود
 * ([[noteMatchEnd]]) — هر پنلِ پایانِ مسابقه این را دارد، با آگهی یا بی آن.
 */
export function ResultAd({ won, watching }: { won: boolean | null; watching: boolean }) {
  const a = useSignal(ads);
  const sub = useSignal(client.subscription);
  const ref = useRef<HTMLElement>(null);
  const [room, setRoom] = useState(0);
  const [h, setH] = useState(0);
  const [gone, setGone] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { noteMatchEnd(tableKey(), won, watching); }, []);

  const tag = gone ? "" : resultTag(sub?.active === true, a);

  // جای خالیِ زیرِ کارت: offsetTop/Height تبدیلِ ورودِ کارت را نمی‌بینند، پس همان جای نهایی.
  useLayoutEffect(() => {
    const el = ref.current;
    const veil = el?.parentElement;
    const card = veil?.querySelector<HTMLElement>(":scope > .vcard, :scope > .mf-sheet, :scope > .results");
    if (!tag || !veil || !card) return;
    const measure = () => {
      const edge = el ? parseFloat(getComputedStyle(el).bottom) || 0 : 0;
      setRoom(veil.clientHeight - (card.offsetTop + card.offsetHeight) - GAP_PX - edge);
    };
    measure();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    ro?.observe(card);
    ro?.observe(veil);
    return () => ro?.disconnect();
  }, [tag]);

  // آگهیِ آمده جا نمی‌شود: برداشته، نه پنهان.
  useEffect(() => {
    if (h > 1 && h > room) { setGone(true); adEvent("ad_fail", "WR", { why: "too_tall" }); }
  }, [h, room]);

  const on = !!a && !!tag && h >= FILLED_PX && h <= room;
  useEffect(() => { if (on) adEvent("ad_start", "WR", { net: "mediaad" }); }, [on]);
  // جا نبود: یک بار، وقتی اندازه رسید.
  const measured = room !== 0;
  useEffect(() => {
    if (tag && measured && room < MIN_ROOM_PX) adEvent("ad_skip_policy", "WR", { why: "no_room" });
  }, [tag, measured]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!a || !tag) return <span ref={ref} hidden />;
  return (
    <aside ref={ref} className={"adslot resultad" + (on ? " on" : "")} aria-label="آگهی"
      style={{ maxBlockSize: Math.max(room, 0) }}>
      {room >= MIN_ROOM_PX && (
        <>
          <span className="adtag">آگهی</span>
          <AdFrame key={tag} domain={a.domain} tag={tag} onHeight={setH} />
        </>
      )}
    </aside>
  );
}
